// useStreamingTreeNode
//
// Per-node binding between a tree row component (TreeViewerItem) and
// the viewer's ViewerStateStore. Works across all viewer types:
// Viewer3d (3D Asset), TimelineViewer (Estimation), and any future
// viewer that renders the same tree shape.
//
// === DATA-SHAPE CONTRACT ===
//
// Tree nodes from helpers/three.js (generateRawTree,
// generateSubprojectTree) have shape:
//
//   {
//     id: string,            // ← group identity for the store
//     parentId: string,
//     key: string,
//     label: string,
//     treeVisible: boolean,  // legacy fallback only
//     meshInfo?: { UniqueId, ... },   // present on leaves only
//     nodes: [...]                    // present on sections; [] on leaves
//   }
//
// SECTION node (no meshInfo, has children):
//   `node.id` is the group identity. operations use
//   store.hideGroup(node.id, leafUids) / store.showGroup(node.id).
//   UI eye reads store.isGroupHidden(node.id) — O(1).
//
// LEAF node (has meshInfo, no children):
//   `node.meshInfo.UniqueId` is the per-uid identity. Operations
//   use store.hide / store.show / store.isHidden directly.
//
// === RETURN VALUE ===
//
//   {
//     isHidden: bool       — eye-icon state (closed when true)
//     isHighlighted: bool  — any descendant has a highlight color
//     hide()               — hide this node + descendants
//     show()               — show this node + descendants
//     toggleHidden()       — flip hide/show
//   }
//
// === LEGACY FALLBACK ===
//
// When `viewer.streaming?.state` is undefined (legacy non-streaming
// projects), the hook reads isHidden from `node.treeVisible` and
// returns no-op operations. The tree row component should keep its
// existing UpdateGroupVisibility / treeVisible mutation path for
// legacy projects and skip it for streaming projects (see
// TreeViewerItem.jsx integration for the conditional).
//
// === RE-RENDER STRATEGY ===
//
// Subscribes to store events. Force re-render only when:
//   - event is an isolate transition (everything's effective state flips)
//   - event uids overlap THIS node's leaf uids
//
// Smart side-detection: iterate the SMALLER of (event uids, node leaves).
// A 1-uid select event arriving at a 10k-leaf section row → 1 Set lookup,
// no leaves walk. A 10k-uid hide event arriving at a 1-leaf section row
// → 1 Set lookup against event uids.
//
// extractLeafUids walks the node once per node-identity change, cached
// in a ref. Lazy: only computed when re-render decision needs it.

import { useCallback, useEffect, useReducer, useRef } from 'react';

// Default leaf-uid extractor for the standard tree shape.
// Iterative to avoid stack overflow on deep trees.
export function extractLeafUids(node) {
  if (!node) return [];
  // A row built from a document's index draws at most a page of its
  // elements but stands for all of them, so it carries the full uid
  // list; hiding or selecting it has to act on every one.
  if (Array.isArray(node.allUids) && node.allUids.length) return node.allUids;
  const out = [];
  const stack = [node];
  while (stack.length > 0) {
    const n = stack.pop();
    if (!n) continue;
    if (n.meshInfo?.UniqueId) {
      out.push(n.meshInfo.UniqueId);
      continue;
    }
    if (Array.isArray(n.nodes) && n.nodes.length > 0) {
      for (let i = n.nodes.length - 1; i >= 0; i--) stack.push(n.nodes[i]);
    }
  }
  return out;
}

export function useStreamingTreeNode({ viewer, node, getLeafUids }) {
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // Cache leafUids against node identity. Recompute only when the node
  // reference actually changes.
  const leafUidsRef = useRef(null);
  const lastNodeRef = useRef(null);
  const lastLoadedRef = useRef(false);
  // A row built from a document's index keeps its identity when its
  // elements arrive, so the cached (empty) uid list has to be dropped on
  // that transition too, not only when the node reference changes.
  const loadedNow = !!(node && node.lazy && node.lazy.loaded);
  if (lastNodeRef.current !== node || lastLoadedRef.current !== loadedNow) {
    leafUidsRef.current = null;
    lastNodeRef.current = node;
    lastLoadedRef.current = loadedNow;
  }
  // Checked at call time as well as at render: a click that loads a row
  // (or resolves a section's elements from the server, helpers/treeRowUids)
  // sets node.allUids and then acts in the same handler, before any
  // re-render - a cache keyed only on render handed it the empty list from
  // before (2026-09-19: the eye on a Category row hid nothing).
  const lastAllUidsRef = useRef(undefined);
  const getLeafUidsCached = useCallback(() => {
    const allUids = node ? node.allUids : undefined;
    const loaded = !!(node && node.lazy && node.lazy.loaded);
    if (allUids !== lastAllUidsRef.current || loaded !== lastLoadedRef.current) {
      leafUidsRef.current = null;
      lastAllUidsRef.current = allUids;
      lastLoadedRef.current = loaded;
    }
    if (leafUidsRef.current === null) {
      const fn = getLeafUids || extractLeafUids;
      leafUidsRef.current = fn(node);
    }
    return leafUidsRef.current;
  }, [node, getLeafUids]);

  // Read store identity here (not just inside the effect) so we can use
  // it as a dep. StreamingLoader._initStreaming is async — viewer is
  // constructed synchronously but `viewer.streaming.state` is attached
  // a few ticks later. The first render typically lands BEFORE that
  // attach, so the effect would otherwise subscribe to a null store and
  // never re-run (viewer/node refs are stable across re-renders, so
  // their dep values don't change). Including the store reference makes
  // the effect re-run the moment streaming state becomes available.
  const store = viewer?.streaming?.state;

  // Subscribe to store events; selectively re-render.
  useEffect(() => {
    if (!store || !node) return undefined;

    const isLeaf = !!node.meshInfo?.UniqueId;
    const leafUid = node.meshInfo?.UniqueId;

    return store.subscribe(({ type, uids }) => {
      // Isolate transitions affect every node's effective visibility
      // (isolate-on hides everything not in the set; isolate-off shows
      // everything previously hidden by isolate). Always re-render.
      if (type === 'isolate' || type === 'clear-isolate') {
        forceUpdate();
        return;
      }

      // Full-sweep marker (uids === null): re-render. Rare — mostly
      // for diagnostic/full-rebuild paths.
      if (!uids) {
        forceUpdate();
        return;
      }

      if (isLeaf) {
        // Direct uid match for leaf rows.
        for (let i = 0; i < uids.length; i++) {
          if (uids[i] === leafUid) {
            forceUpdate();
            return;
          }
        }
        return;
      }

      // Section row: any leaf overlap → re-render. Iterate smaller side.
      const leaves = getLeafUidsCached();
      if (leaves.length === 0) return;

      if (uids.length < leaves.length) {
        const leafSet = new Set(leaves);
        for (let i = 0; i < uids.length; i++) {
          if (leafSet.has(uids[i])) {
            forceUpdate();
            return;
          }
        }
      } else {
        const eventSet = new Set(uids);
        for (let i = 0; i < leaves.length; i++) {
          if (eventSet.has(leaves[i])) {
            forceUpdate();
            return;
          }
        }
      }
    });
  }, [store, node, getLeafUidsCached]);

  // ---- Derived state (read fresh from store on every render) -------

  const groupId = node?.id;
  const leafUid = node?.meshInfo?.UniqueId;

  // isHidden:
  //   leaf    → store.isHidden(uid)
  //   section → isGroupHidden(this group) OR every descendant leaf is hidden
  //   legacy  → node.treeVisible
  //
  // The descendant-bubble check is what makes the eye icon recursive in
  // BOTH directions of the tree. When a top-level section's hideGroup
  // call adds all descendant leaves to the hidden set, the intermediate
  // sections in between aren't tracked as their own groups
  // (hideGroup uses ONE groupId — the clicked node's id), so
  // isGroupHidden would return false for them. Without the bubble-up,
  // those intermediate rows would render an open eye even though every
  // mesh under them is invisible. The leaves.every(...) check fixes
  // that: a section that has zero visible descendants reads as hidden.
  //
  // Cost: O(L) per render where L = descendant leaves, with a Set
  // membership check per leaf. The hook already caches leaf lists per
  // node identity (leafUidsRef), so the walk is only a tight loop over
  // the cached array — fine for the tree sizes we render.
  let isHidden;
  if (!store) {
    isHidden = node?.treeVisible === false;
  } else if (leafUid) {
    isHidden = store.isHidden(leafUid);
  } else if (store.isGroupHidden(groupId)) {
    isHidden = true;
  } else {
    const leaves = getLeafUidsCached();
    if (leaves.length === 0) {
      isHidden = false;
    } else {
      isHidden = true;
      for (let i = 0; i < leaves.length; i++) {
        if (!store.isHidden(leaves[i])) {
          isHidden = false;
          break;
        }
      }
    }
  }

  // isHighlighted: any leaf has a highlight color in the store.
  let isHighlighted = false;
  if (store) {
    if (leafUid) {
      isHighlighted = !!store.getHighlight(leafUid);
    } else {
      const leaves = getLeafUidsCached();
      for (let i = 0; i < leaves.length; i++) {
        if (store.getHighlight(leaves[i])) {
          isHighlighted = true;
          break;
        }
      }
    }
  }

  // ---- Operations --------------------------------------------------

  const hide = useCallback(() => {
    if (!store) return;
    if (leafUid) {
      store.hide(leafUid);
      return;
    }
    const leaves = getLeafUidsCached();
    if (leaves.length === 0) return;
    // hideGroup adds every descendant uid to the hidden set AND records
    // the group → uids mapping so the eye icon can answer "is this
    // section hidden" in O(1) via isGroupHidden(groupId). One emit, one
    // applicator pass, regardless of how many leaves the section owns.
    store.hideGroup(groupId, leaves);
  }, [store, leafUid, groupId, getLeafUidsCached]);

  const show = useCallback(() => {
    if (!store) return;
    if (leafUid) {
      store.show(leafUid);
      return;
    }
    // Section show needs to be recursive in the strong sense: every
    // descendant leaf becomes visible, regardless of how it got hidden.
    //
    // showGroup alone only undoes the hideGroup of THIS group — uids
    // hidden by other groups or by an individual store.hide(uid) call
    // would stay hidden. That's not the behavior a tree user expects
    // when they click the eye on a section "show all of these".
    //
    // Wrap both operations in a batch so the two emits coalesce into a
    // single applicator pass (one GPU sweep, not two). beginBatch /
    // endBatch are inert when there's no active batch, but here we're
    // explicitly opening one.
    const leaves = getLeafUidsCached();
    store.beginBatch();
    try {
      store.showGroup(groupId);
      if (leaves.length > 0) store.bulkShow(leaves);
    } finally {
      store.endBatch();
    }
  }, [store, leafUid, groupId, getLeafUidsCached]);

  const toggleHidden = useCallback(() => {
    if (isHidden) show();
    else hide();
  }, [isHidden, hide, show]);

  return {
    isHidden,
    isHighlighted,
    hide,
    show,
    toggleHidden,
    isStreamingActive: !!store,
  };
}